import { ROLES } from '../../../components/User/constants';
import { GlobalPermissions } from '../../../context/GlobalPermissions/types';
import { GetPermittedUserRolesQuery, SearchUser, SearchUserEdge, User, UserOrganizationInfoQuery } from '../types';

export const mockUserByOrganization: Pick<
  User,
  'assignments' | 'email' | 'firstName' | 'lastName' | 'id' | 'phone' | 'roleList' | 'status'
> = {
  id: 'user-id-2',
  firstName: 'John',
  lastName: 'Doe',
  email: 'JohnDoe@disney.com',
  phone: '+1 (123) 456-7890',
  roleList: [
    {
      role: 'Organization Admin',
      entityId: '578cc416-8fba-4694-a264-c54519094766',
      entityType: 'MARKETER_ORGANIZATION',
      entityName: {
        id: '578cc416-8fba-4694-a264-c54519094766',
      },
    },
  ],
  status: 'ACTIVE',
  assignments: [
    {
      adAccountId: '-',
      adAccountName: 'All',
      assignment: 'ORGANIZATION_ADMIN',
    },
  ],
};

export const mockUsersByOrgIdAndAdAccountId: SearchUserEdge[] = [
  {
    cursor: 'cursor-1',
    node: {
      createdAt: '09/10/2024',
      deletable: false,
      email: 'LisaSmith@disney.com',
      firstName: 'Lisa',
      id: 'user-id-2',
      lastName: 'Smith',
      lastLoginAt: '09/10/2024',
      organizations: [],
      organizationsCount: 0,
      phone: '',
      role: 'Organization Admin',
      status: 'ACTIVE',
      updatedAt: '09/10/2024',
    },
  },
  {
    cursor: 'cursor-2',
    node: {
      createdAt: '08/01/2023',
      deletable: false,
      email: 'JohnDoe@disney.com',
      firstName: 'John',
      id: 'user-id-1',
      lastName: 'Doe',
      lastLoginAt: '08/01/2023',
      organizations: [],
      organizationsCount: 0,
      phone: '',
      role: 'Organization Admin',
      status: 'ACTIVE',
      updatedAt: '08/01/2023',
    },
  },
  {
    cursor: 'cursor-3',
    node: {
      createdAt: '10/01/2023',
      deletable: false,
      email: 'JaneLee@disney.com',
      firstName: 'Jane',
      id: 'user-id-3',
      lastName: 'Lee',
      lastLoginAt: '10/01/2023',
      organizations: [],
      organizationsCount: 0,
      phone: '',
      role: 'Member',
      status: 'ACTIVE',
      updatedAt: '10/01/2023',
    },
  },
];

export const mockPermittedRoles: GetPermittedUserRolesQuery = {
  getPermittedUserRoles: {
    organizationRoles: [
      {
        name: 'Organization Admin',
        displayName: 'Admin',
      },
      {
        name: 'Organization Member',
        displayName: 'Member',
      },
    ],
    adAccountRoles: [
      {
        name: 'Ad Account Admin',
        displayName: 'Admin',
      },
      {
        name: 'Buyer',
        displayName: 'Buyer',
      },
      {
        name: 'Ad Account Analyst',
        displayName: 'Analyst',
      },
    ],
    adAccounts: [
      {
        id: 'adAccount-id-1',
        name: 'Demo Account 1',
      },
      {
        id: 'adAccount-id-2',
        name: 'Demo Account 2',
      },
    ],
    creativeLibraryId: 'creative-library-id-1',
  },
};

export const mockUserByIdAndOrganization: UserOrganizationInfoQuery = {
  userOrganizationInfo: {
    id: 'user-id-1',
    firstName: 'John',
    lastName: 'Doe',
    createdAt: '09/10/2024',
    lastLoginAt: '09/10/2024',
    deletable: true,
    email: 'JohnDoe@disney.com',
    phone: '+1 (123) 456-7890',
    roleList: [
      {
        role: 'Organization Admin',
        entityId: '578cc416-8fba-4694-a264-c54519094766',
        entityType: 'MARKETER_ORGANIZATION',
        entityName: {
          id: '578cc416-8fba-4694-a264-c54519094766',
        },
      },
    ],
    status: 'ACTIVE',
  },
};

export const mockUserByOrgId: SearchUser[] = [
  {
    createdAt: '09/10/2024',
    deletable: true,
    firstName: 'John',
    lastName: 'Doe',
    lastLoginAt: '09/10/2024',
    email: 'JohnDoe@disney.com',
    id: 'user-id-1',
    organizations: [],
    organizationsCount: 0,
    role: 'Organization Admin',
    status: 'ACTIVE',
    updatedAt: '09/10/2024',
    phone: '+1 (123) 456-7890',
  },
];

export const mockMyPermissions: GlobalPermissions = {
  roles: [ROLES.admin],
  adAccountPermissions: undefined,
  permissions: {
    __typename: 'Permissions',
    ADMIN_CREATE_PAYMENTS: true,
    ADMIN_DELETE_PAYMENTS: true,
    ADMIN_READ_PAYMENTS: true,
    ADMIN_RETRY_PAYMENTS: true,
    ADMIN_REFUND_PAYMENTS: true,
    ADMIN_UPDATE_PAYMENTS: true,
    ADMIN_UPDATE_THRESHOLD: true,
    APPLY_PROMO_CODE: true,
    BUILD_REPORT: true,
    CANCEL_LINE_ITEM: true,
    CLONE_LINE_ITEM: true,
    CONFIGURE_AD_TAG: true,
    CREATE_AD_ACCOUNT: true,
    CREATE_ASSET: true,
    CREATE_BRAND: true,
    CREATE_BROADCAST: true,
    CREATE_CAMPAIGN: true,
    CREATE_CREATIVE: true,
    CREATE_INDUSTRY: true,
    CREATE_LINEITEM: true,
    CREATE_PAYMENTS: true,
    CREATE_REPORT: true,
    CREATE_USER: true,
    DELETE_AD_ACCOUNT: true,
    DELETE_BROADCAST: true,
    DELETE_CAMPAIGN: true,
    DELETE_CREATIVE: true,
    DELETE_CREATIVES: true,
    DELETE_LINE_ITEM: true,
    DELETE_ORGANIZATION: true,
    DELETE_PAYMENTS: true,
    DELETE_REPORT: true,
    DETACH_PROMO_CODE: true,
    GET_CAMPAIGN: true,
    GET_LINE_ITEM: true,
    INVITE_USER: true,
    PAUSE_LINE_ITEM: true,
    READ_AD_ACCOUNT: true,
    READ_ALL_ADS: true,
    READ_ALL_CAMPAIGNS: true,
    READ_ALL_LINE_ITEMS: true,
    READ_BRAND: true,
    READ_BROADCAST: true,
    READ_CAMPAIGN_LINEITEMS: true,
    READ_CREATIVE: true,
    READ_CREATIVES: true,
    READ_INDUSTRY: true,
    READ_LEGACY_REPORT: true,
    READ_NOTIFICATION: true,
    READ_ORGANIZATION: true,
    READ_OWN_USER: true,
    READ_PAYMENTS: true,
    READ_REPORT_TEMPLATE: true,
    READ_REPORT: true,
    READ_ROLES: true,
    READ_TARGETING: true,
    READ_USER: true,
    READ_USERS: true,
    RESUME_LINE_ITEM: true,
    SUBMIT_LINE_ITEM: true,
    SUSPEND_LINE_ITEM: false,
    UPDATE_AD_ACCOUNT_BILLING_CONTACT: true,
    UPDATE_AD_ACCOUNT_BRAND: true,
    UPDATE_AD_ACCOUNT_NAME: true,
    UPDATE_AD_ACCOUNT: true,
    UPDATE_BRAND: true,
    UPDATE_BROADCAST: true,
    UPDATE_CAMPAIGN: true,
    UPDATE_INDUSTRY: true,
    UPDATE_LINE_ITEM: true,
    UPDATE_NOTIFICATION_SETTINGS: true,
    UPDATE_ORGANIZATION: true,
    UPDATE_PAYMENTS: true,
    UPDATE_REPORT: true,
    UPDATE_USER: true,
    USER_STATUS_UPDATE: true,
    OVERRIDE_LINE_ITEM_PRICE: true,
    CREATE_KERV_CREATIVE: false,
    UPDATE_KERV_CREATIVE: false,
  },
};
